import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject, Observable, catchError, firstValueFrom, map, of } from 'rxjs'
import { SessionStorageService } from 'src/app/login/services/session-storage.service'
import { AuthService } from 'src/app/shared/auth/auth.service'
import { ObservableCacheService } from 'src/app/shared/services/observable-cache.service'
import { RedirectTo } from 'src/app/utils/redirect-to'
import { BusinessProfileService as ApiBusinessProfileService } from '../../../shared/services/backend'

@Injectable({
    providedIn: 'root',
})
export class ProductionEquipmentAccessService {
    private authService = inject(AuthService)
    private sessionStorageService = inject(SessionStorageService)
    private profileService = inject(ApiBusinessProfileService)
    private router = inject(Router)
    private cache = inject(ObservableCacheService)

    // Cache configuration
    private readonly CACHE_DURATION_MS = 15 * 60 * 1000 // 15 minutes

    private productionEquipment: BehaviorSubject<boolean> = new BehaviorSubject(false)
    public productionEquipment$: Observable<boolean> = this.productionEquipment.asObservable()
    public loading$: Observable<boolean> = of(false)

    public async getProductionEquipmentAccess(forceRefresh: boolean = false) {
        if (!this.authService.isPrivateCustomer) {
            this.loading$ = of(!this.router.url.includes('profile')) // Set loader to true if not Profile page
            let response = await firstValueFrom(this.getProductionEquipmentAccessCached$(forceRefresh))
            this.productionEquipment.next(response)
            this.loading$ = of(false)
        } else {
            this.productionEquipment.next(false)
        }
    }

    private getProductionEquipmentAccessCached$(forceRefresh: boolean = false): Observable<boolean> {
        let customerNumber = this.sessionStorageService.customerNumber
        let installationNumber = this.sessionStorageService.installationNumber

        if (!customerNumber || !installationNumber) {
            return of(false)
        }

        const cacheKey = this.cache.createKey('productionEquipment', customerNumber, installationNumber)

        return this.cache.getOrSet(
            cacheKey,
            () =>
                this.profileService
                    .apiProfileBusinessCustomerCustomerNumberProductionConsumptionInstallationNumberGet(
                        customerNumber,
                        installationNumber
                    )
                    .pipe(
                        map((i) => i.hasProductionEquipment),
                        catchError((error) => {
                            this.handleError(error)
                            // Clear cache on error
                            this.cache.clear(cacheKey)
                            return of(false)
                        })
                    ),
            { forceRefresh, cacheDuration: this.CACHE_DURATION_MS }
        )
    }

    /**
     * Clears production equipment cache for a specific installation
     */
    public clearProductionEquipmentCache(customerNumber: string, installationNumber: string): void {
        const cacheKey = this.cache.createKey('productionEquipment', customerNumber, installationNumber)
        this.cache.clear(cacheKey)
    }

    /**
     * Clears all production equipment cache entries
     */
    public clearAllProductionEquipmentCache(): void {
        this.cache.clearByPattern(/^productionEquipment:/)
    }

    private handleError(error) {
        RedirectTo.internalServerError(this.router, error)
    }
}
